import React, { useState } from 'react';
import { API_BASE } from '../config/apiConfig';

/**
 * Comm-AI — Public-facing MCP/A2A bridge.
 *
 * This module represents the business to the outside world:
 * - Discover external services via MCP tools (vendors, contractors, legal)
 * - Send/receive A2A messages to external agents
 * - Manage SignWell agreements
 * - View outgoing request audit trail
 *
 * Data boundary: this layer NEVER accesses the private LLM/RAG data directly.
 * It receives sanitized requests from the LLM layer.
 */
const AgenticAI = ({ clientConfig }) => {
  const [query, setQuery] = useState('');
  const [urgency, setUrgency] = useState('Medium');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('discover');

  const handleServiceDiscovery = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      // In production, this calls the agentic dispatch endpoint
      // For now, show the request structure that would be sent
      const serviceRequest = {
        jsonrpc: '2.0',
        id: `req-${Date.now()}`,
        method: 'discovery.find_provider',
        params: {
          query,
          urgency,
          context: {
            task: query,
            location_ref: 'Site-A',
          },
          callback: {
            mcp_endpoint: `${API_BASE}/mcp/v1/callback`,
            auth_token_ref: `pnp/clients/${clientConfig?.client_id}/mcp-callback-token`,
          },
        },
      };
      setResult({ type: 'dispatch', data: serviceRequest });
    } catch {
      setResult({ type: 'error', data: 'Failed to dispatch request' });
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'discover', label: '🔍 Service Discovery', desc: 'Find vendors, contractors, and services via MCP' },
    { id: 'a2a', label: '🤝 A2A Messages', desc: 'Agent-to-agent communication with external partners' },
    { id: 'agreements', label: '📝 Agreements', desc: 'Send and track SignWell e-signature documents' },
    { id: 'audit', label: '📋 Audit Trail', desc: 'All outgoing A2A requests and MCP tool invocations' },
  ];

  return (
    <div style={{ padding: '2rem' }}>
      <h2 style={{ marginBottom: '0.25rem' }}>🐙 Comm-AI — Public Bridge</h2>
      <p style={{ color: '#666', marginBottom: '1.5rem' }}>
        Represents <strong>{clientConfig?.company_name}</strong> to the outside world via MCP tools and A2A agents.
        Only public information is shared — internal LLM/RAG data never leaves the private perimeter.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #ddd', cursor: 'pointer',
              background: activeTab === tab.id ? '#2c3e50' : '#fff',
              color: activeTab === tab.id ? '#fff' : '#333',
              fontSize: '0.85rem', fontWeight: activeTab === tab.id ? 600 : 400,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'discover' && (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '12px', padding: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>MCP Service Discovery</h3>
          <p style={{ color: '#888', fontSize: '0.85rem' }}>
            Search for external service providers using the MCP protocol. Results come from registered
            vendor catalogs, service marketplaces, and A2A agent networks.
          </p>
          <form onSubmit={handleServiceDiscovery} style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="e.g. Licensed plumber for kitchen leak repair"
              style={{ flex: 1, minWidth: '250px', padding: '0.75rem', borderRadius: '8px', border: '1px solid #ddd', fontSize: '0.9rem' }}
              aria-label="Service search query"
            />
            <select value={urgency} onChange={e => setUrgency(e.target.value)} style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid #ddd' }} aria-label="Urgency level">
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
              <option value="Emergency">Emergency</option>
            </select>
            <button type="submit" disabled={loading} style={{
              padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', cursor: 'pointer',
              background: '#2c3e50', color: '#fff', fontWeight: 600,
            }}>
              {loading ? 'Searching...' : 'Search via MCP'}
            </button>
          </form>

          {result && (
            <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '1rem', marginTop: '1rem' }}>
              <h4 style={{ marginTop: 0, fontSize: '0.85rem', color: '#666' }}>
                {result.type === 'dispatch' ? '📤 A2A Service Request (JSON-RPC 2.0)' : '❌ Error'}
              </h4>
              <pre style={{ background: '#2c3e50', color: '#e0e0e0', padding: '1rem', borderRadius: '8px', overflow: 'auto', fontSize: '0.8rem' }}>
                {JSON.stringify(result.data, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}

      {activeTab === 'a2a' && (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '12px', padding: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>A2A Agent Communication</h3>
          <p style={{ color: '#888' }}>
            Horizontal agent-to-agent messaging with external partners. Incoming permission requests
            from distributors appear here for review.
          </p>
          <div style={{ padding: '2rem', textAlign: 'center', color: '#aaa' }}>
            No active A2A conversations. External agents will appear here when they initiate contact via the MCP/A2A protocol.
          </div>
        </div>
      )}

      {activeTab === 'agreements' && (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '12px', padding: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>SignWell Agreements</h3>
          <p style={{ color: '#888' }}>
            Draft, send, and track e-signature documents. Templates are configured from your AgenticAI.pdf.
          </p>
          <div style={{ padding: '2rem', textAlign: 'center', color: '#aaa' }}>
            No pending agreements. Use Service Discovery to find a provider, then send an agreement for signature.
          </div>
        </div>
      )}

      {activeTab === 'audit' && (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '12px', padding: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>Public Audit Trail</h3>
          <p style={{ color: '#888' }}>
            Every outgoing A2A request and MCP tool invocation is logged here for operator review and CMMC compliance.
          </p>
          <div style={{ padding: '2rem', textAlign: 'center', color: '#aaa' }}>
            No audit entries yet. Entries appear when the Comm-AI dispatches external requests.
          </div>
        </div>
      )}
    </div>
  );
};

export default AgenticAI;
